/**
 * LootContainerEvents - Handles loot container networking
 */
import { ClientLogger } from '../../logger/ClientLogger.js';

const lootEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {}, debug: () => {} };
})();
const lootEventsLogger = ClientLogger.getLogger('loot') || lootEventsLoggerFallback;

const LootContainerEvents = {
  register(socket, gameState, callbacks = {}) {
    const {
      showNotification,
      loadWorldObjectImage,
      playSpatialSound,
      resetLocalInteraction
    } = callbacks;

    const logPrefix = '[LootContainer][Events]';
    const log = (...args) => lootEventsLogger.debug(logPrefix, ...args);

    const getInteractionState = () => {
      if (!gameState.lootContainerInteraction) {
        gameState.lootContainerInteraction = {
          activeContainerId: null,
          progress: 0,
          holdDurationMs: 0,
          isHolding: false
        };
      }
      return gameState.lootContainerInteraction;
    };

    const updateWorldObject = (containerId, worldObject) => {
      if (!containerId) return;
      const existing = gameState.worldObjects?.get(containerId);
      const merged = { ...(existing || {}), ...(worldObject || {}) };
      if (!merged.id) {
        merged.id = containerId;
      }
      if (merged.isHidden) {
        merged.opacity = 0;
      } else if (existing && existing.opacity === 0) {
        merged.opacity = 1;
      }
      gameState.worldObjects?.set(containerId, merged);
      // Solidity may have changed (idle ↔ opening/opened): rebuild movement colliders
      gameState.rebuildColliders?.();
      if (typeof loadWorldObjectImage === 'function' && merged.graphic) {
        loadWorldObjectImage(merged);
      }
    };

    const upsertContainer = (containerId, containerData = {}) => {
      if (!containerId) return;
      const existing = gameState.lootContainers.get(containerId) || {};
      const merged = { ...existing, ...containerData };
      gameState.lootContainers.set(containerId, merged);
    };

    const resetInteractionIfMatches = (containerId) => {
      if (!resetLocalInteraction) return;
      if (!containerId) return;
      if (getInteractionState().activeContainerId === containerId) {
        resetLocalInteraction();
        log('Resetting local interaction for', containerId);
        lastProgressLog = null;
      }
    };

    let lastProgressLog = null;
    const maybeLogProgress = (containerId, progress) => {
      if (typeof progress !== 'number') return;
      const shouldLog = progress === 0
        || progress === 1
        || lastProgressLog === null
        || Math.abs(progress - lastProgressLog) >= 0.25;
      if (!shouldLog) return;
      lastProgressLog = progress;
      log('Hold progress', `${Math.round(progress * 100)}%`, 'for', containerId);
    };

    socket.on('lootContainerSpawned', (data) => {
      if (!data?.containerId) return;
      upsertContainer(data.containerId, data.container);
      updateWorldObject(data.containerId, data.worldObject);
    });

    socket.on('lootContainerRespawned', (data) => {
      if (!data?.containerId) return;
      upsertContainer(data.containerId, data.container);
      updateWorldObject(data.containerId, data.worldObject);
      resetInteractionIfMatches(data.containerId);
    });

    socket.on('lootContainerRemoved', (data) => {
      if (!data?.containerId) return;
      gameState.lootContainers.delete(data.containerId);
      gameState.worldObjects?.delete?.(data.containerId);
      resetInteractionIfMatches(data.containerId);
      if (gameState.nearestLootContainerId === data.containerId) {
        gameState.nearestLootContainerId = null;
        gameState.nearestLootContainerDistance = null;
      }
    });

    socket.on('lootContainerStateUpdate', (data) => {
      if (!data?.containerId) return;
      upsertContainer(data.containerId, {
        state: data.state,
        remainingHits: data.remainingHits,
        maxHits: data.maxHits
      });
      updateWorldObject(data.containerId, data.worldObject);
    });

    socket.on('lootContainerProgress', (data) => {
      if (!data || data.playerId !== gameState.localId) return;
      const interaction = getInteractionState();
      interaction.activeContainerId = data.containerId;
      interaction.progress = data.progress || 0;
      interaction.isHolding = true;
      const now = (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
      interaction.lastServerProgressMs = now;
      if (!interaction.localStartTime && interaction.holdDurationMs) {
        interaction.localStartTime = now - (interaction.progress * interaction.holdDurationMs);
      }
      maybeLogProgress(data.containerId, interaction.progress);
    });

    socket.on('lootContainerInteract', (data) => {
      if (!data || data.playerId !== gameState.localId) return;
      const interaction = getInteractionState();
      interaction.activeContainerId = data.containerId;
      interaction.holdDurationMs = data.holdDuration || 0;
      interaction.progress = 0;
      interaction.isHolding = true;
      interaction.localStartTime = (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
      interaction.lastServerProgressMs = 0;
      lastProgressLog = null;
      log('Interaction started for', data.containerId, `holdDuration=${interaction.holdDurationMs}ms`);
    });

    socket.on('lootContainerInteractCancel', (data) => {
      if (!data || data.playerId !== gameState.localId) return;
      resetLocalInteraction?.();
      lastProgressLog = null;
      log('Interaction cancelled for', data.containerId, 'reason: server_cancel');
    });

    socket.on('lootContainerOpening', (data) => {
      if (!data?.containerId) return;
      log('Container opening', data.containerId, 'type:', data.containerType, `duration=${data.animationDuration}ms`);
      resetInteractionIfMatches(data.containerId);
    });

    socket.on('lootContainerOpened', (data) => {
      if (!data?.containerId) return;
      log('Container opened', data.containerId, 'loot items:', data.loot?.length ?? 0);
      resetInteractionIfMatches(data.containerId);
    });

    socket.on('lootContainerDamaged', (data) => {
      if (!data?.containerId) return;
      upsertContainer(data.containerId, {
        remainingHits: data.remainingHits,
        maxHits: data.maxHits,
        lastHitAt: performance.now()
      });
    });

    socket.on('lootContainerSound', (data) => {
      if (!data?.soundPath || typeof playSpatialSound !== 'function') return;
      playSpatialSound(data.soundPath, data.x, data.y, data.volume);
    });

    socket.on('lootContainerInteractFailed', (data) => {
      resetLocalInteraction?.();
      lastProgressLog = null;
      log('Interaction failed', data?.containerId, 'reason:', data?.reason);
      if (typeof showNotification === 'function' && data?.reason) {
        showNotification(`Cannot use container: ${data.reason}`, 'warning');
      }
    });
  }
};

export { lootEventsLoggerFallback, lootEventsLogger, LootContainerEvents };
